/**
 * Runs work while driving a 0–100 upload progress value.
 * Pads with a short minimum duration so fast local picks still feel intentional.
 *
 * @param {object} options
 * @param {(value: number | null) => void} options.setProgress
 * @param {() => (void | Promise<void>)} [options.work]
 * @param {() => (void | Promise<void>)} [options.onReady] — apply result just before 100%
 * @param {number} [options.minMs=1400]
 * @param {number} [options.holdCompleteMs=200]
 */
export async function runWithMinUploadProgress({
  setProgress,
  work = null,
  onReady = null,
  minMs = 1400,
  holdCompleteMs = 200,
} = {}) {
  if (typeof setProgress !== "function") {
    await work?.();
    await onReady?.();
    return;
  }

  const start = performance.now();
  let value = 8;
  setProgress(value);

  const tick = setInterval(() => {
    value = Math.min(90, value + 3.5 + Math.random() * 5);
    setProgress(Math.round(value));
  }, 70);

  try {
    await work?.();
  } finally {
    clearInterval(tick);
    const elapsed = performance.now() - start;
    const remain = Math.max(0, minMs - elapsed);
    if (remain > 0) {
      await new Promise((resolve) => setTimeout(resolve, remain));
    }
    try {
      await onReady?.();
    } catch (err) {
      setProgress(null);
      throw err;
    }
    setProgress(100);
    if (holdCompleteMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, holdCompleteMs));
    }
    setProgress(null);
  }
}
